import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../helpers/api';
import { ENDPOINTS } from '../helpers/constants';
import { wasUpdatedWithinValidityWindow } from '../helpers/serviceVersionHelpers';

/**
 * Fetches service versions for the active order and owns Request Update UI flow.
 */
const useRequestUpdate = ({
  activeOrder,
  activeOrderTabKey,
  service,
  onServiceUpdate,
  setAlertModalMeta
} = {}) => {
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  const fetchVersions = useCallback(async orderId => {
    if (!orderId) {
      setVersions([]);
      setLoadingVersions(false);
      return;
    }
    setLoadingVersions(true);
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.SERVICE_VERSIONS_BY_ORDER(orderId),
        method: 'GET'
      });
      if (error) {
        setVersions([]);
        return;
      }
      setVersions(Array.isArray(response) ? response : []);
    } finally {
      setLoadingVersions(false);
    }
  }, []);

  useEffect(() => {
    if (activeOrderTabKey === 'survey') {
      setVersions([]);
      return;
    }
    fetchVersions(activeOrder?._id);
  }, [activeOrder?._id, activeOrderTabKey, fetchVersions]);

  const openModal = useCallback(() => setShowModal(true), []);
  const closeModal = useCallback(() => {
    if (!submitting) setShowModal(false);
  }, [submitting]);

  const onRequestUpdateClick = useCallback(() => {
    const { showWarning, recentMoment } = wasUpdatedWithinValidityWindow(versions);
    if (showWarning && recentMoment) {
      setAlertModalMeta?.({
        title: '⚠ Recent Update Found',
        alertText: `An update was already created on ${recentMoment.format('MM/DD/YYYY')}. Do you still need to create a new update?`,
        dismissButtonText: 'No, cancel',
        continueButtonText: 'Yes, create new update',
        continueButtonVariant: 'warning',
        onContinueClick: () => {
          setAlertModalMeta?.(null);
          setShowModal(true);
        }
      });
      return;
    }
    setShowModal(true);
  }, [versions, setAlertModalMeta]);

  const onRequestUpdateSubmit = useCallback(
    async values => {
      if (!activeOrder?._id) {
        toast.error('Order not found');
        return;
      }

      setSubmitting(true);
      try {
        const { response, error } = await makeRESTApiRequest({
          endpoint: ENDPOINTS.SERVICE_VERSIONS,
          method: 'POST',
          requestBody: {
            orderId: activeOrder._id,
            closingDate: values.newClosingDate,
            reason: values.reason === 'Other (specify in notes)' ? '' : values.reason,
            notes: values.notes
          }
        });

        if (error) {
          toast.error(error);
          return;
        }

        const nextOrders = [...(service?.orders || [])];
        const orderIndex = nextOrders.findIndex(o => o._id === activeOrder._id);
        if (orderIndex !== -1) {
          nextOrders[orderIndex] = {
            ...nextOrders[orderIndex],
            ...(response?.order || {}),
            status: response?.order?.status || 'Update'
          };
        }

        onServiceUpdate?.({
          ...service,
          orders: nextOrders,
          closingDate: response?.service?.closingDate || values.newClosingDate
        });

        await fetchVersions(activeOrder._id);
        setShowModal(false);
        toast.success(`${response?.version?.versionLabel || 'Update'} created successfully`);
      } catch (e) {
        toast.error(e?.message || 'Failed to create update');
      } finally {
        setSubmitting(false);
      }
    },
    [activeOrder?._id, service, onServiceUpdate, fetchVersions]
  );

  return {
    versions,
    loadingVersions,
    fetchVersions,
    showModal,
    submitting,
    openModal,
    closeModal,
    onRequestUpdateClick,
    onRequestUpdateSubmit
  };
};

export default useRequestUpdate;
